import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { getSecondHandItems, getCategories } from '../../api';
import { useAuth } from '../../context/AuthContext';
import SecondHandCard, { CONDITION_LABELS } from '../../components/common/SecondHandCard';
import { FiSearch, FiFilter, FiX, FiChevronLeft, FiChevronRight, FiPlus, FiShield, FiAlertCircle } from 'react-icons/fi';
import '../Products.css'; // shared filter bar / grid / pagination styles
import './SecondHand.css';

const PAGE_SIZE = 12;

const SecondHand = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const search = searchParams.get('search') || '';
  const category = searchParams.get('category') || '';
  const condition = searchParams.get('condition') || '';
  const ordering = searchParams.get('ordering') || '-created_at';

  const fetchItems = useCallback(() => {
    setLoading(true);
    const params = { page, page_size: PAGE_SIZE, ordering };
    if (search) params.search = search;
    if (category) params.category = category;
    if (condition) params.condition = condition;
    setLoadError(false);
    getSecondHandItems(params)
      .then(({ data }) => {
        setItems(data.results || []);
        setCount(data.count || 0);
      })
      .catch(() => {
        setItems([]);
        setCount(0);
        setLoadError(true);
      })
      .finally(() => setLoading(false));
  }, [page, search, category, condition, ordering]);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => { getCategories().then(({ data }) => setCategories(data.results || [])).catch(() => {}); }, []);
  useEffect(() => { setPage(1); }, [search, category, condition, ordering]);

  const setParam = (key, val) => {
    const p = new URLSearchParams(searchParams);
    if (val) p.set(key, val); else p.delete(key);
    setSearchParams(p);
  };

  const totalPages = Math.ceil(count / PAGE_SIZE);

  return (
    <div className="secondhand-page">
      {/* Banner */}
      <section className="sh-hero">
        <div className="container sh-hero-inner">
          <div>
            <div className="sh-hero-eyebrow">Certified Pre-Owned Marketplace</div>
            <h1 className="sh-hero-title">Second Hand <span className="text-red">DJI</span></h1>
            <p className="sh-hero-sub">
              Buy and sell used DJI drones, gimbals and accessories. Every listing is
              reviewed by our team before it goes live.
            </p>
            <div className="sh-hero-actions">
              <Link to={user ? '/second-hand/sell' : '/signin'} className="btn btn-primary">
                <FiPlus /> Sell Your DJI Gear
              </Link>
              {user && (
                <Link to="/dashboard?tab=listings" className="btn btn-outline">My Listings</Link>
              )}
            </div>
          </div>
          <div className="sh-hero-note">
            <FiShield />
            <div>
              <strong>Admin verified</strong>
              <span>Listings are manually approved before publishing</span>
            </div>
          </div>
        </div>
      </section>

      <div className="container">
        <div className="products-header">
          <div>
            <h2 className="page-title">Browse Listings</h2>
            <p className="text-muted">{count} pre-owned item{count === 1 ? '' : 's'} available</p>
          </div>
          <button className="btn btn-outline btn-sm filter-toggle" onClick={() => setFilterOpen(!filterOpen)}>
            <FiFilter /> Filters {filterOpen ? <FiX /> : null}
          </button>
        </div>

        {/* Filter Bar */}
        <div className={`filter-bar ${filterOpen ? 'open' : ''}`}>
          <div className="filter-search">
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search used gear..."
              value={search}
              onChange={(e) => setParam('search', e.target.value)}
            />
            {search && <button onClick={() => setParam('search', '')}><FiX /></button>}
          </div>

          <select value={category} onChange={(e) => setParam('category', e.target.value)}>
            <option value="">All Categories</option>
            {categories.map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}
          </select>

          <select value={condition} onChange={(e) => setParam('condition', e.target.value)}>
            <option value="">Any Condition</option>
            {Object.entries(CONDITION_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>

          <select value={ordering} onChange={(e) => setParam('ordering', e.target.value)}>
            <option value="-created_at">Newest First</option>
            <option value="created_at">Oldest First</option>
            <option value="price">Price: Low to High</option>
            <option value="-price">Price: High to Low</option>
          </select>

          {(search || category || condition) && (
            <button className="btn btn-ghost btn-sm" onClick={() => setSearchParams({})}>
              <FiX /> Clear All
            </button>
          )}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="skeleton" style={{ height: 340, borderRadius: 8 }} />
            ))}
          </div>
        ) : loadError ? (
          <div className="no-results">
            <div className="no-results-icon"><FiAlertCircle /></div>
            <h3>Marketplace unavailable</h3>
            <p className="text-muted">
              We could not reach the second-hand service. Please try again in a moment.
            </p>
            <button className="btn btn-primary" onClick={fetchItems}>Retry</button>
          </div>
        ) : items.length === 0 ? (
          <div className="no-results">
            <div className="no-results-icon">♻️</div>
            <h3>No second-hand items yet</h3>
            <p className="text-muted">
              {search || category || condition
                ? 'Try adjusting your search or filters'
                : 'Be the first to list your pre-owned DJI gear.'}
            </p>
            <Link to={user ? '/second-hand/sell' : '/signin'} className="btn btn-primary">
              <FiPlus /> List an Item
            </Link>
          </div>
        ) : (
          <div className="grid-4">
            {items.map((item) => <SecondHandCard key={item.id} item={item} />)}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination">
            <button
              className="btn btn-outline btn-sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <FiChevronLeft /> Prev
            </button>
            <span className="page-info">Page {page} of {totalPages}</span>
            <button
              className="btn btn-outline btn-sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next <FiChevronRight />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SecondHand;
